import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldAlert, Save } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canCasesAuthor, useAuth } from "@/lib/auth";
import { CASES_V2 } from "@/constants/testIds";

const CREATED_BY_OPTIONS = ["Teena", "Reception", "Super Admin"];

// V2.0 — Customer Type replaces the old 5-option Customer Source. The two
// choices map to the existing backend customer_source enum values so no
// backend/schema change is required:
//   already_bought → customer_source="rentopc" (linked to an existing sale;
//                     warranty auto-calc uses the linked SO when present)
//   paid           → customer_source="other"    (paid_service warranty)
const CUSTOMER_TYPES = [
  { v: "already_bought", l: "Already Bought", source: "rentopc" },
  { v: "paid",           l: "Paid",           source: "other" },
];

const WARR_TONE = {
  in_warranty:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  as_per_contract: "bg-sky-50 text-sky-700 border-sky-200",
  paid_service:    "bg-slate-50 text-slate-700 border-slate-200",
  expired_warranty: "bg-rose-50 text-rose-700 border-rose-200",
  out_of_warranty: "bg-rose-50 text-rose-700 border-rose-200",
  battery_warranty: "bg-amber-50 text-amber-700 border-amber-200",
  no_battery_warranty: "bg-slate-50 text-slate-600 border-slate-200",
};

function Field({ label, children, required, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

const inputCls = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800";

export default function NewCaseV2Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = canCasesAuthor(user);
  useEffect(() => {
    if (user && !allowed) navigate("/admin/cases", { replace: true });
  }, [allowed, user, navigate]);

  // V1.2 — When opened from a Sales Invoice, prefill the form with all known
  // customer/laptop/link info. Reception only needs to enter the complaint.
  const [search] = useSearchParams();
  const linkFromSO = useMemo(() => ({
    passport_id: search.get("passport_id") || null,
    sales_order_id: search.get("sales_order_id") || null,
    invoice_no: search.get("invoice_no") || null,
    customer_id: search.get("customer_id") || null,
  }), [search]);

  const [f, setF] = useState(() => ({
    created_by_label: "Reception",
    // V2.0 — customer_type is the new UI-level control. When submitting we
    // translate it to the legacy customer_source enum (already_bought→rentopc,
    // paid→other) so the backend contract is unchanged. Default is Already
    // Bought; a linked SO from the URL naturally stays on this branch.
    customer_type: "already_bought",
    customer_name: search.get("customer_name") || "",
    mobile: search.get("mobile") || "",
    alternate_number: search.get("alternate_number") || "",
    email: search.get("email") || "",
    company_name: search.get("company_name") || "",
    gst_number: search.get("gst_number") || "",
    address: search.get("address") || "",
    city: search.get("city") || "",
    state: search.get("state") || "",
    pincode: search.get("pincode") || "",
    brand: search.get("brand") || "",
    model: search.get("model") || "",
    manufacturer_serial: search.get("manufacturer_serial") || "",
    processor: search.get("processor") || "",
    ram: search.get("ram") || "",
    storage: search.get("storage") || "",
    generation: search.get("generation") || "",
    charger_included: true,
    complaint: "",
    has_battery_problem: false,
    password_protected: false,
  }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // V2.0 — When "Already Bought" is chosen, Reception must pick an existing
  // sale/customer BEFORE saving. `linkedSaleId` holds the currently-selected
  // link and is required to enable the submit button.
  const [linkedSaleId, setLinkedSaleId] = useState(linkFromSO.sales_order_id || null);
  useEffect(() => { setLinkedSaleId(linkFromSO.sales_order_id || null); }, [linkFromSO.sales_order_id]);

  // Effective backend customer_source (kept in sync with customer_type).
  const effectiveSource = useMemo(() => {
    const t = CUSTOMER_TYPES.find((x) => x.v === f.customer_type);
    return t ? t.source : "other";
  }, [f.customer_type]);

  const [warranty, setWarranty] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.post("/cases-v2/warranty-preview", {
          customer_source: effectiveSource,
        });
        if (!cancel) setWarranty(data);
      } catch {
        if (!cancel) setWarranty(null);
      }
    })();
    return () => { cancel = true; };
  }, [effectiveSource]);

  // V2.0 — Sale/customer search for Already Bought. Debounced, hits the
  // existing /api/search endpoint (types=sales,customer). No new backend.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchTouched, setSearchTouched] = useState(false);
  useEffect(() => {
    if (f.customer_type !== "already_bought") { setSearchResults([]); return; }
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/search", {
          params: { q, types: "sales,customer", limit: 10 },
        });
        if (!cancel) {
          setSearchResults(Array.isArray(data?.items) ? data.items : []);
          setSearchTouched(true);
        }
      } catch {
        if (!cancel) { setSearchResults([]); setSearchTouched(true); }
      } finally {
        if (!cancel) setSearching(false);
      }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [searchQuery, f.customer_type]);

  // Pick an existing sale → fetch full detail and autofill.
  const pickSale = async (soId) => {
    try {
      const { data: d } = await api.get(`/sales/${soId}`);
      const firstLine = (d.lines && d.lines[0]) || {};
      setF((p) => ({
        ...p,
        customer_name: d.bill_name || p.customer_name,
        mobile: d.bill_mobile || p.mobile,
        email: d.bill_email || p.email,
        company_name: d.bill_company_name || p.company_name,
        gst_number: d.bill_gstin || p.gst_number,
        address: [d.bill?.address_line1, d.bill?.address_line2]
          .filter(Boolean).join(", ") || p.address,
        city: d.bill?.city || p.city,
        state: d.bill?.state || p.state,
        pincode: d.bill?.pincode || p.pincode,
        brand: firstLine.brand || p.brand,
        model: firstLine.model || p.model,
        manufacturer_serial: firstLine.passport_serial || p.manufacturer_serial,
        processor: firstLine.processor || p.processor,
        ram: firstLine.ram_gb ? String(firstLine.ram_gb) : p.ram,
        storage: firstLine.storage_gb ? String(firstLine.storage_gb) : p.storage,
        generation: firstLine.generation || p.generation,
      }));
      setLinkedSaleId(soId);
      // Stash the extra link IDs so submit() can send them.
      pickedLinkRef.current = {
        sales_order_id: d.id,
        invoice_no: d.invoice_no || null,
        customer_id: d.customer_id || null,
        passport_id: firstLine.passport_id || null,
      };
      setSearchQuery("");
      setSearchResults([]);
    } catch (e) {
      setError(errorToMessage(e, "Could not load the selected sale"));
    }
  };
  // Ref (not state) to avoid an extra render loop on link updates.
  const pickedLinkRef = useMemo(() => ({ current: {
    passport_id: linkFromSO.passport_id || null,
    sales_order_id: linkFromSO.sales_order_id || null,
    invoice_no: linkFromSO.invoice_no || null,
    customer_id: linkFromSO.customer_id || null,
  } }), [linkFromSO]);

  const clearPickedSale = () => {
    setLinkedSaleId(null);
    pickedLinkRef.current = { passport_id: null, sales_order_id: null,
      invoice_no: null, customer_id: null };
    setSearchTouched(false);
  };

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // V2.0 — "Already Bought" requires a linked existing sale before saving.
  // "Paid" allows manual entry.
  const isAlready = f.customer_type === "already_bought";
  // Validate the mobile's DIGITS (still exactly 10) rather than its exact
  // formatting. Customer mobiles autofilled from an existing Sale are often
  // stored with a space (e.g. "98917 24598"), which is a valid 10-digit number
  // but failed the previous /^\d{10}$/ test and left "Create case" disabled.
  // This preserves the 10-digit requirement (backend accepts any string) while
  // not forcing re-entry of contact info the Sale already provides.
  const mobileDigits = f.mobile.replace(/\D/g, "");
  const canSubmit = f.customer_name.trim()
    && mobileDigits.length === 10
    && f.complaint.trim().length >= 3
    && (!isAlready || !!linkedSaleId);

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true); setError(null);
    try {
      // Translate customer_type → legacy customer_source for the backend.
      const body = {
        ...f,
        customer_source: effectiveSource,
        ...pickedLinkRef.current,
      };
      // customer_type is a UI-only field; the backend rejects unknown fields
      // in strict schemas so drop it before sending.
      delete body.customer_type;
      const { data } = await api.post("/cases-v2", body);
      navigate(`/admin/cases/${data.id}`);
    } catch (e) {
      setError(errorToMessage(e, "Could not create case"));
    } finally {
      setSubmitting(false);
    }
  };

  const warrTone = warranty ? (WARR_TONE[warranty.status] || WARR_TONE.paid_service) : "";

  return (
    <div className="space-y-6 pb-10" data-testid={CASES_V2.newPage}>
      <div>
        <button onClick={() => navigate("/admin/cases")}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft size={14} /> Cases
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">New Customer Case (V2)</h1>
        <p className="text-sm text-slate-500 mt-1">
          Reception intake — Warranty is auto-calculated from the linked Sales Invoice / customer source when available; for pure walk-in cases select the warranty manually. Complete Contents, Condition, Photos and Password on the case detail screen after saving.
        </p>
      </div>

      {/* Meta */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Created By" required>
          <select value={f.created_by_label} data-testid={CASES_V2.createdByLabel}
            onChange={(e) => set("created_by_label", e.target.value)} className={inputCls}>
            {CREATED_BY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Customer Type" required>
          <div className="flex gap-2" data-testid={CASES_V2.customerType}>
            {CUSTOMER_TYPES.map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={o.v === "already_bought" ? CASES_V2.customerTypeAlready : CASES_V2.customerTypePaid}
                onClick={() => {
                  set("customer_type", o.v);
                  if (o.v === "paid") clearPickedSale();
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${
                  f.customer_type === o.v
                    ? "bg-slate-900 border-slate-900 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </Field>
        <div className="flex items-end">
          <div data-testid={CASES_V2.warrantyPreview}
            className={`w-full rounded-md border px-3 py-2.5 text-sm flex items-center gap-2 ${warrTone}`}>
            {warranty?.status === "in_warranty" ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
            <span className="font-semibold">
              {warranty ? warranty.status.replaceAll("_", " ") : "—"}
            </span>
            {warranty?.days != null && (
              <span className="text-xs text-slate-600 ml-1">
                {warranty.days === 0 ? "Paid" : `${warranty.days}d · ends ${warranty.end_date || "?"}`}
              </span>
            )}
            {warranty?.days == null && warranty && (
              <span className="text-xs text-slate-600 ml-1">as per contract</span>
            )}
          </div>
        </div>
      </section>

      {/* V2.0 — Already Bought: search & link an existing sale/customer */}
      {isAlready && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Search existing sale or customer</h2>
            {linkedSaleId && (
              <button
                type="button"
                data-testid={CASES_V2.customerSearchClear}
                onClick={clearPickedSale}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
              >
                Clear linked sale
              </button>
            )}
          </div>
          {linkedSaleId ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Linked to sale <span className="font-mono">{pickedLinkRef.current.invoice_no || pickedLinkRef.current.sales_order_id}</span> — customer details autofilled below.
            </div>
          ) : (
            <>
              <input
                value={searchQuery}
                data-testid={CASES_V2.customerSearchInput}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by phone, customer name, or SO number…"
                className={inputCls}
              />
              {searchQuery.trim().length >= 2 && (
                <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {searching && (
                    <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
                  )}
                  {!searching && searchResults.length === 0 && searchTouched && (
                    <div
                      data-testid={CASES_V2.customerSearchEmpty}
                      className="px-3 py-3 text-xs text-slate-600 bg-amber-50 border-t border-amber-200"
                    >
                      No matching sale or customer found. Please go to
                      <span className="font-semibold"> Sales</span> and create/record
                      the sale first — a customer cannot be created from this screen.
                    </div>
                  )}
                  {!searching && searchResults.map((r) => (
                    <button
                      key={`${r.type}-${r.id}`}
                      type="button"
                      data-testid={CASES_V2.customerSearchResult(r.id)}
                      onClick={() => {
                        if (r.type === "sales") {
                          pickSale(r.id);
                        } else if (r.type === "customer") {
                          // Customer-only hit: prefill the customer fields but
                          // require Reception to pick a specific sale next.
                          setF((p) => ({
                            ...p,
                            customer_name: r.title || p.customer_name,
                            mobile: (r.subtitle && r.subtitle.match(/\d{10}/)?.[0]) || p.mobile,
                          }));
                        }
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-3"
                    >
                      <div>
                        <div className="font-medium text-slate-800">{r.title}</div>
                        {r.subtitle && (
                          <div className="text-xs text-slate-500">{r.subtitle}</div>
                        )}
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">{r.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Customer */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Customer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Customer Name" required>
            <input value={f.customer_name} data-testid={CASES_V2.customerName}
              onChange={(e) => set("customer_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Mobile" required hint="10-digit number">
            <input value={f.mobile} data-testid={CASES_V2.mobile}
              inputMode="numeric" maxLength={10}
              onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} className={inputCls} />
          </Field>
          <Field label="Alternate Number">
            <input value={f.alternate_number} data-testid={CASES_V2.altMobile}
              inputMode="numeric" maxLength={10}
              onChange={(e) => set("alternate_number", e.target.value.replace(/\D/g, ""))} className={inputCls} />
          </Field>
          <Field label="Email">
            <input value={f.email} data-testid={CASES_V2.email}
              onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Company Name">
            <input value={f.company_name} data-testid={CASES_V2.companyName}
              onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="GST">
            <input value={f.gst_number} data-testid={CASES_V2.gst}
              onChange={(e) => set("gst_number", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address">
            <input value={f.address} data-testid={CASES_V2.address}
              onChange={(e) => set("address", e.target.value)} className={inputCls} />
          </Field>
          <Field label="City">
            <input value={f.city} data-testid={CASES_V2.city}
              onChange={(e) => set("city", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State">
              <input value={f.state} data-testid={CASES_V2.state}
                onChange={(e) => set("state", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Pincode">
              <input value={f.pincode} data-testid={CASES_V2.pincode}
                inputMode="numeric" maxLength={6}
                onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} className={inputCls} />
            </Field>
          </div>
        </div>
      </section>

      {/* Laptop */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Laptop Details</h2>
        <p className="text-xs text-slate-500">
          Manufacturer Serial is only mandatory later if the laptop powers on. Enter it now if available.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Brand"><input value={f.brand} data-testid={CASES_V2.brand}
            onChange={(e) => set("brand", e.target.value)} className={inputCls} /></Field>
          <Field label="Model"><input value={f.model} data-testid={CASES_V2.model}
            onChange={(e) => set("model", e.target.value)} className={inputCls} /></Field>
          <Field label="Manufacturer Serial"><input value={f.manufacturer_serial} data-testid={CASES_V2.serial}
            onChange={(e) => set("manufacturer_serial", e.target.value)} className={inputCls} /></Field>
          <Field label="Processor"><input value={f.processor} data-testid={CASES_V2.processor}
            onChange={(e) => set("processor", e.target.value)} className={inputCls} /></Field>
          <Field label="RAM"><input value={f.ram} data-testid={CASES_V2.ram}
            onChange={(e) => set("ram", e.target.value)} className={inputCls} /></Field>
          <Field label="Storage"><input value={f.storage} data-testid={CASES_V2.storage}
            onChange={(e) => set("storage", e.target.value)} className={inputCls} /></Field>
          <Field label="Generation"><input value={f.generation} data-testid={CASES_V2.generation}
            onChange={(e) => set("generation", e.target.value)} className={inputCls} /></Field>
          <div className="flex items-end gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={f.charger_included} data-testid={CASES_V2.chargerIncluded}
                onChange={(e) => set("charger_included", e.target.checked)}
                className="rounded border-slate-300" />
              Charger Included
            </label>
          </div>
        </div>
      </section>

      {/* Complaint */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Problem</h2>
        <Field label="Complaint Description" required>
          <textarea value={f.complaint} data-testid={CASES_V2.complaint}
            rows={4} onChange={(e) => set("complaint", e.target.value)}
            placeholder="Describe every problem inline. This will be printed on the label — it wraps automatically."
            className={inputCls} />
        </Field>
        <div className="flex flex-wrap items-center gap-6">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.has_battery_problem} data-testid={CASES_V2.hasBatteryProblem}
              onChange={(e) => set("has_battery_problem", e.target.checked)}
              className="rounded border-slate-300" />
            Battery Problem selected (forces mandatory Battery Engineer step)
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.password_protected} data-testid={CASES_V2.passwordProtected}
              onChange={(e) => set("password_protected", e.target.checked)}
              className="rounded border-slate-300" />
            Password Protected (fill on detail page)
          </label>
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button data-testid={CASES_V2.cancel} onClick={() => navigate("/admin/cases")}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button data-testid={CASES_V2.submit} disabled={!canSubmit || submitting}
          onClick={submit}
          className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
          <Save size={14} /> {submitting ? "Creating…" : "Create case"}
        </button>
      </div>
    </div>
  );
}
